/**
 * StudentLiveHelp — « Un problème ? » pendant le live (`<LiveHelpSheet>`) : choisir son problème, ce
 * que l'écran a vérifié et ce qu'on peut essayer, puis lever la main, écrire au tuteur ou signaler.
 *
 * Le parcours d'aujourd'hui (`TroubleshootingPanel` puis `LiveHelpModal`), sur les mêmes appels :
 * le signalement part par `POST api/v2/live-sessions/{id}/issue-reports` (`category`,
 * `description`, `diagnostics.reconnections`, `platform`), avec les mêmes mesures Amplitude
 * (`bug_report.modal.opened`, `bug_report.submitted`). Les pièces jointes (images et vidéos, trois
 * au plus, 20 Mo par image et 100 Mo par vidéo : la galerie sur mobile, un fichier ou le
 * glisser-déposer sur le web) sont jugées dès leur choix — une pièce trop lourde ou d'un autre format est refusée aussitôt, et la phrase
 * le dit au-dessus du bouton —, puis partent au stockage avant le signalement, leurs clés avec lui
 * (`attachment_files`, `issueReportAttachments.ts`) ; un échec se dit au même endroit, le signalement
 * reste rempli.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import React, { useEffect, useRef, useState } from "react"
import { Platform } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { liveServiceV2 } from "../../../../../../data/services/liveServiceV2"
import { LiveHelpSheet } from "../../../../../../design-system/student"
import { apiCall } from "../../../../../../engine/shared/apiCall"
import { type EngineTelemetry } from "../../../../../../engine/shared/telemetry"
import { useMediaPickerAdapter } from "../../../hooks/useMediaPickerAdapter"
import { type UILiveProblem, type UILiveReportCategory } from "../../../types/studentLive"
import { liveHelpDiagnostic, type LiveHelpContext } from "../../../../view-models/student/Live/studentLiveViewModel"
import {
    ISSUE_REPORT_MAX_ATTACHMENTS,
    ReportAbandonedError,
    admitPickedAttachments,
    issueReportFailureMessage,
    uploadIssueAttachments,
} from "./issueReportAttachments"

/** Le `formId` des pièces du signalement — celui de l'ancienne modale. */
const ISSUE_REPORT_FORM_ID = "issue_report"
const IS_WEB = Platform.OS === "web"
const ATTACH_LABEL = "Joindre une capture ou une vidéo"
/** L'ajout se tient compact sous la description : un bouton, pas la grande zone de dépôt. */
const ATTACH_HEIGHT = 72

type PickedMedia = { uri: string; width?: number; height?: number; mimeType?: string; name?: string; fileSize?: number }

type Props = {
    visible: boolean
    /** Le problème que l'écran voit déjà (la connexion faible) : la feuille s'ouvre dessus. */
    initialProblem: UILiveProblem | null
    context: LiveHelpContext
    liveSessionId: number | null
    onClose: () => void
    onRaiseHand: () => void
    onOpenChat: () => void
    /** Le signalement est parti. */
    onReported: () => void
    telemetry: EngineTelemetry
}

type RootState = { liveMode?: { todayLiveSessionLogs?: { action?: string }[] | null } }

export function StudentLiveHelp(props: Props) {
    const { visible, initialProblem, context, liveSessionId, onClose, telemetry } = props
    const dispatch = useDispatch()
    const logs = useSelector((state: RootState) => state.liveMode?.todayLiveSessionLogs)
    const [step, setStep] = useState<"choose" | "diagnostic" | "report">("choose")
    const [problem, setProblem] = useState<UILiveProblem | null>(null)
    const [category, setCategory] = useState<UILiveReportCategory | null>(null)
    const [description, setDescription] = useState("")
    const [images, setImages] = useState<PickedMedia[]>([])
    const [sending, setSending] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // Le jeton de l'envoi en cours : fermer la feuille l'abandonne, et son issue ne touche plus rien
    // (elle réapparaissait à la réouverture, ou refermait une feuille rouverte entre-temps).
    const sendToken = useRef(0)

    // Une pièce choisie est jugée aussitôt (format ; 20 Mo une image, 100 Mo une vidéo) : refusée,
    // elle n'entre pas, et la phrase le dit, limite comprise, avant l'envoi.
    const admit = (next: PickedMedia[]) => {
        const { kept, refusal } = admitPickedAttachments(images, next)
        setImages(kept)
        setError(refusal)
    }

    // Les pièces : images et vidéos, la galerie sur mobile, un fichier sur le web, et le
    // glisser-déposer ; pas d'appareil photo en plein live (l'ancienne modale n'en avait pas).
    const picker = useMediaPickerAdapter({
        images,
        setImages: admit,
        formId: ISSUE_REPORT_FORM_ID,
        multiple: true,
        // La galerie ne laisse choisir que ce qui reste de place ; au-delà, la phrase le dit.
        selectionLimit: ISSUE_REPORT_MAX_ATTACHMENTS - images.length,
        enableVideo: true,
        enableCamera: false,
        enableExternalLink: false,
        enableDocument: IS_WEB,
        buttonLabel: ATTACH_LABEL,
        addMoreButtonLabel: ATTACH_LABEL,
        showAddMoreButton: images.length < ISSUE_REPORT_MAX_ATTACHMENTS,
    })
    const media = { ...picker, minHeight: ATTACH_HEIGHT }

    useEffect(() => {
        if (!visible) return
        setProblem(initialProblem)
        setStep(initialProblem != null ? "diagnostic" : "choose")
    }, [visible, initialProblem])

    const reset = () => {
        sendToken.current += 1
        setSending(false)
        setCategory(null)
        setDescription("")
        setImages([])
        setError(null)
        setStep("choose")
    }

    const send = () => {
        if (liveSessionId == null || category == null || sending) return
        setSending(true)
        setError(null)
        const token = ++sendToken.current
        const current = () => token === sendToken.current
        const joins = (logs ?? []).filter((log) => log?.action === "joined_call").length
        // Fermer la feuille abandonne l'envoi : aucune pièce de plus ne part, et le signalement n'est pas
        // soumis (sans quoi il partait sans confirmation, et rouvrir pour renvoyer en faisait deux). La
        // pièce en cours de route, elle, finit son envoi : `uploadAsync` ne s'interrompt pas, et sa clé
        // reste au stockage temporaire sans signalement.
        uploadIssueAttachments(images, undefined, current)
            .then((keys) => {
                if (!current()) throw new ReportAbandonedError()
                return apiCall(dispatch as never, liveServiceV2.submitIssueReport, {
                    routeParams: { id: liveSessionId },
                    data: {
                        category,
                        description,
                        diagnostics: { reconnections: Math.max(0, joins - 1) },
                        platform: Platform.OS,
                        ...(keys.length > 0 ? { attachment_files: keys } : {}),
                    },
                })
            })
            .then(() => {
                amplitude.track("bug_report.submitted")
                if (!current()) return
                reset()
                onClose()
                props.onReported()
            })
            .catch((cause: unknown) => {
                if (cause instanceof ReportAbandonedError) return
                telemetry.error("issue_report", cause)
                if (current()) setError(issueReportFailureMessage(cause))
            })
            .finally(() => {
                if (current()) setSending(false)
            })
    }

    return (
        <LiveHelpSheet
            visible={visible}
            onClose={() => {
                reset()
                onClose()
            }}
            step={step}
            problem={problem}
            onSelectProblem={(selected) => {
                setProblem(selected)
                setStep("diagnostic")
            }}
            onBack={() => setStep(step === "report" ? (problem != null ? "diagnostic" : "choose") : "choose")}
            diagnostic={liveHelpDiagnostic(problem, context)}
            onRaiseHand={() => {
                onClose()
                props.onRaiseHand()
            }}
            onOpenChat={() => {
                onClose()
                props.onOpenChat()
            }}
            onReport={() => {
                amplitude.track("bug_report.modal.opened")
                setStep("report")
            }}
            report={{
                category,
                onSelectCategory: setCategory,
                description,
                onChangeDescription: setDescription,
                onSend: send,
                sending,
                media,
                error,
            }}
        />
    )
}
